
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Calendar, DollarSign } from 'lucide-react';
import TreatmentSearchModalV2 from './TreatmentSearchModalV2';
import VisitBlockV2 from './VisitBlockV2';
import CreditBlock from '@/components/molecules/CreditBlock';
import { Treatment, CheckoutTreatment, Visit, TreatmentPlan } from '@/types/supabase-schema';

interface TreatmentPlanBuilderV2Props {
  onPlanChange: (plan: TreatmentPlan | null) => void;
  creditBalance: number;
  isMember: boolean;
}

const TreatmentPlanBuilderV2 = ({ onPlanChange, creditBalance, isMember }: TreatmentPlanBuilderV2Props) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [patientInfo, setPatientInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const calculateTotals = () => {
    const totalCost = visits.reduce((sum, visit) => sum + visit.totalCost, 0);
    const totalDuration = visits.reduce((sum, visit) => sum + visit.estimatedDuration, 0);
    const creditsNeeded = Math.ceil(totalCost / 50);
    const memberSavings = visits.reduce((sum, visit) => {
      return sum + visit.treatments.reduce((treatmentSum, treatment) => {
        if (isMember && treatment.member_price < treatment.retail_price) {
          return treatmentSum + ((treatment.retail_price - treatment.member_price) * treatment.quantity);
        }
        return treatmentSum;
      }, 0);
    }, 0);

    return {
      totalCost,
      totalDuration,
      creditsNeeded,
      memberSavings,
      creditsSufficient: creditBalance >= creditsNeeded
    };
  };

  const totals = calculateTotals();

  // Update parent when plan changes
  React.useEffect(() => {
    if (visits.length > 0 && patientInfo.name) {
      const plan: TreatmentPlan = {
        id: `plan-${Date.now()}`,
        patient_id: 'temp-patient',
        patient_info: patientInfo,
        visits,
        totals,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      onPlanChange(plan);
    } else {
      onPlanChange(null);
    }
  }, [visits, patientInfo, totals, onPlanChange]);

  const addNewVisit = () => {
    const newVisit: Visit = {
      id: `visit-${Date.now()}`,
      visitNumber: visits.length + 1,
      title: `Visit ${visits.length + 1}`,
      scheduledDate: '',
      treatments: [],
      totalCost: 0,
      estimatedDuration: 0,
      notes: ''
    };
    setVisits([...visits, newVisit]);
  };

  return (
    <div className="space-y-6">
      {/* Patient Information */}
      <Card className="border-juv-rose-clay/20 bg-white">
        <CardHeader>
          <CardTitle className="text-juv-deep-chestnut">Patient Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Patient Name"
              value={patientInfo.name}
              onChange={(e) => setPatientInfo({ ...patientInfo, name: e.target.value })}
              className="px-3 py-2 border border-juv-rose-clay/30 rounded-md focus:outline-none focus:ring-2 focus:ring-juv-terracotta"
            />
            <input
              type="email"
              placeholder="Email Address"
              value={patientInfo.email}
              onChange={(e) => setPatientInfo({ ...patientInfo, email: e.target.value })}
              className="px-3 py-2 border border-juv-rose-clay/30 rounded-md focus:outline-none focus:ring-2 focus:ring-juv-terracotta"
            />
            <input
              type="tel"
              placeholder="Phone Number"
              value={patientInfo.phone}
              onChange={(e) => setPatientInfo({ ...patientInfo, phone: e.target.value })}
              className="px-3 py-2 border border-juv-rose-clay/30 rounded-md focus:outline-none focus:ring-2 focus:ring-juv-terracotta"
            />
          </div>
        </CardContent>
      </Card>

      {/* Credit Balance Display */}
      <CreditBlock 
        balance={creditBalance}
        isMember={isMember}
        creditsNeeded={totals.creditsNeeded}
        creditsSufficient={totals.creditsSufficient}
      />

      {/* Treatment Plan Builder */}
      <Card className="border-juv-rose-clay/20 bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-juv-deep-chestnut">Treatment Timeline</CardTitle>
            <Button
              onClick={() => setIsSearchOpen(true)}
              className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Treatment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {visits.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-juv-deep-chestnut/50 mb-4">
                <Calendar className="h-16 w-16 mx-auto mb-4" />
                <p className="text-lg font-medium">No visits planned yet</p>
                <p className="text-sm">Add treatments to create your first visit</p>
              </div>
              <Button
                onClick={() => setIsSearchOpen(true)}
                variant="outline"
                className="text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
              >
                <Search className="h-4 w-4 mr-2" />
                Browse Treatments
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {visits.map((visit) => (
                <VisitBlockV2
                  key={visit.id}
                  visit={visit}
                  isMember={isMember}
                  onUpdate={(updatedVisit) => {
                    setVisits(visits.map(v => v.id === visit.id ? updatedVisit : v));
                  }}
                  onRemove={() => {
                    setVisits(visits.filter(v => v.id !== visit.id));
                  }}
                  onAddTreatment={() => setIsSearchOpen(true)}
                />
              ))}
              
              <Button
                onClick={addNewVisit}
                variant="outline"
                className="w-full text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Visit
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Summary */}
      {visits.length > 0 && (
        <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
          <CardHeader>
            <CardTitle className="text-juv-terracotta">Investment Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">{visits.length}</div>
                <div className="text-sm text-juv-deep-chestnut/70">Visits</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">{totals.totalDuration}m</div>
                <div className="text-sm text-juv-deep-chestnut/70">Duration</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">{totals.creditsNeeded}</div>
                <div className="text-sm text-juv-deep-chestnut/70">Credits</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">${totals.totalCost.toFixed(2)}</div>
                <div className="text-sm text-juv-deep-chestnut/70">Total</div>
              </div>
            </div>
            
            {totals.memberSavings > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-center">
                  <span className="text-green-800 font-medium">
                    Member Savings: ${totals.memberSavings.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Treatment Search Modal */}
      <TreatmentSearchModalV2
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        treatments={[]}
        onAddTreatment={(treatment) => {
          console.log('Selected treatment:', treatment);
        }}
        currentVisitNumber={visits.length || 1}
        showMemberPricing={isMember}
      />
    </div>
  );
};

export default TreatmentPlanBuilderV2;
